/**
 * LE BANDEAU « PENNYLANE LU À … » — lot 153, 03/11/2026.
 *
 * Posé en tête de la Comptabilité. À l'ouverture, il regarde quand la copie des
 * factures clients a été lue EN ENTIER pour la dernière fois ; au-delà de
 * quinze minutes, il la relit lui-même (`copieAJour`, `pennylaneFrais.ts`).
 * « Actualiser » la relit à la demande. Quand une lecture finit, il prévient
 * l'écran, qui relit ses factures : leur état « Payée » vient de cette copie.
 *
 * La lecture passe par `lireJusquauBout` et la route de l'Analyse
 * (`POST /analyse/pennylane/lectures`) : une seule façon de lire Pennylane.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { lireEtatDesSources, lireUnMorceauPennylane } from './apiAnalyse.js';
import { copieAJour, derniereLectureDite } from './pennylaneFrais.js';
import { lireJusquauBout, type EtatSource, type RessourceSource } from './sources.js';

export interface ProprietesBandeauPennylane {
  readonly jeton: () => Promise<string | null>;
  readonly quandAccesPerdu: () => void;
  /** Appelé quand une lecture vient de se terminer. */
  readonly quandRelu?: () => void;
}

export function VueBandeauPennylane(p: {
  readonly lecture: boolean;
  readonly heure: string | null;
  readonly refus: string | null;
  readonly quandActualiser: () => void;
}) {
  return (
    <div className="bandeau-pennylane">
      <span className="bp-etat">
        {p.lecture
          ? 'Lecture de Pennylane…'
          : p.heure === null
            ? 'Pennylane jamais lu en entier'
            : `Pennylane lu à ${p.heure}`}
      </span>
      <button
        className="bouton bouton-discret bouton-petit"
        type="button"
        disabled={p.lecture}
        onClick={p.quandActualiser}
      >
        Actualiser
      </button>
      {p.refus !== null && <span className="bp-refus">{p.refus}</span>}
    </div>
  );
}

export function BandeauPennylane({ jeton, quandAccesPerdu, quandRelu }: ProprietesBandeauPennylane) {
  const [etats, setEtats] = useState<readonly EtatSource[]>([]);
  const [lecture, setLecture] = useState(false);
  const [refus, setRefus] = useState<string | null>(null);
  // UNE SEULE LECTURE À LA FOIS : un second clic pendant la première
  // doublerait les appels à Pennylane, dont le débit est compté.
  const enCours = useRef(false);

  const jetonOuSortie = useCallback(async (): Promise<string | null> => {
    const j = await jeton();
    if (j === null) quandAccesPerdu();
    return j;
  }, [jeton, quandAccesPerdu]);

  const lireEtats = useCallback(async (): Promise<readonly EtatSource[] | null> => {
    const j = await jetonOuSortie();
    if (j === null) return null;
    const r = await lireEtatDesSources(j);
    if (r.etat !== 'ok') {
      if (r.cause === 'acces') quandAccesPerdu();
      setRefus('L’état de la copie Pennylane n’a pas pu être lu.');
      return null;
    }
    setEtats(r.valeur);
    return r.valeur;
  }, [jetonOuSortie, quandAccesPerdu]);

  const relire = useCallback(async () => {
    if (enCours.current) return;
    enCours.current = true;
    setLecture(true);
    setRefus(null);
    const lireMorceau = async (r: RessourceSource, suite: string | null) => {
      const j = await jetonOuSortie();
      if (j === null) return null;
      const lu = await lireUnMorceauPennylane(j, r, suite);
      if (lu.etat === 'ok') return lu.valeur;
      if (lu.cause === 'acces') quandAccesPerdu();
      return null;
    };
    const fin = await lireJusquauBout('factures_clients', lireMorceau, () => undefined);
    if (fin.issue !== 'complete') {
      setRefus(fin.message ?? 'Pennylane n’a pas pu être lu en entier.');
    } else {
      // LOT 158 : les dates de règlement des factures devenues payées, pour
      // les Encaissements (décision du 03/11 : « toujours à jour »).
      const dates = await lireJusquauBout('reglements_clients', lireMorceau, () => undefined);
      if (dates.issue === 'refusee' || dates.issue === 'injoignable') {
        setRefus(dates.message ?? 'Les dates de règlement n’ont pas pu être lues.');
      }
    }
    await lireEtats();
    setLecture(false);
    enCours.current = false;
    quandRelu?.();
  }, [jetonOuSortie, lireEtats, quandAccesPerdu, quandRelu]);

  // À L'OUVERTURE : relire seulement si la copie n'est pas à jour.
  useEffect(() => {
    let vivant = true;
    void (async () => {
      const lus = await lireEtats();
      if (vivant && lus !== null && !copieAJour(lus, new Date())) await relire();
    })();
    return () => {
      vivant = false;
    };
    // Une seule fois par ouverture de l'écran.
  }, []);

  return (
    <VueBandeauPennylane
      lecture={lecture}
      heure={derniereLectureDite(etats, new Date())}
      refus={refus}
      quandActualiser={() => void relire()}
    />
  );
}
